import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { macrosEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { setInitialOptions } from '@/redux/macros/actions';
import { Macro, Macros } from '@/shared/types/macros';

import { useDeskId } from './useDeskId';
import { createEntityCache } from './utils/entityCache';

export const API = Object.freeze({
  listMacros: async (deskId: string): Promise<Macros> => callGet(endpoints.macros(deskId)),

  getMacro: async (deskId: string, macroId: string): Promise<Macro> =>
    callGet(endpoints.macro(deskId, macroId)),

  createMacro: async (deskId: string, macro: Macro): Promise<Macro> =>
    callPost(endpoints.macros(deskId), macro),

  updateMacro: async (deskId: string, macro: Macro): Promise<Macro> =>
    callPut(endpoints.macro(deskId, macro.macro_id), macro),

  deleteMacro: async (deskId: string, macroId: string): Promise<Macro> =>
    callDelete(endpoints.macro(deskId, macroId)),
});

export const macroCache = createEntityCache<Macro, 'macros'>({
  listKey: (deskId) => [endpoints.macros(deskId)],
  detailKey: (deskId, macroId) => [endpoints.macro(deskId, macroId)],
  listField: 'macros',
  getId: (macro) => macro.macro_id,
  getParentId: (macro) => macro.desk_id,
});

export const useMacros = (macroId?: string) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    data: macros,
    status: listStatus,
    isSuccess: macrosSuccess,
  } = useQuery<Macros, Error>({
    queryKey: [endpoints.macros(deskId)],
    queryFn: () => API.listMacros(deskId),
    enabled: !!deskId,
  });
  useEffect(() => {
    if (macrosSuccess) {
      dispatch(setInitialOptions(macros));
    }
  }, [macrosSuccess, macros, dispatch]);

  const { data: macro, status: macroStatus } = useQuery<Macro, Error>({
    queryKey: [endpoints.macro(deskId, macroId)],
    queryFn: () => API.getMacro(deskId, macroId),
    enabled: !!macroId && !!deskId,
    initialData: macros?.macros.find((macro) => macro.macro_id === macroId),
  });

  const { mutate: updateMacro, status: updateStatus } = useMutation<Macro, Error, Macro>({
    mutationFn: (macro) => API.updateMacro(deskId, macro),
    onSuccess: (resp) => {
      macroCache.updated(queryClient, resp, { parentId: deskId });
      toast.success(t('macros.macro_updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: addMacro, status: addStatus } = useMutation<Macro, Error, Macro>({
    mutationFn: (macro) => API.createMacro(deskId, macro),
    onSuccess: (resp) => {
      macroCache.created(queryClient, resp, { parentId: deskId });
      toast.success(t('macros.macro_created', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: deleteMacro,
    mutateAsync: deleteMacroAsync,
    status: deleteStatus,
  } = useMutation<Macro, Error, string>({
    mutationFn: (macroId) => API.deleteMacro(deskId, macroId),
    onSuccess: (resp) => {
      macroCache.removed(queryClient, { parentId: deskId, id: resp.macro_id });
      toast.success(t('macros.macro_deleted'));
    },
    meta: { errorToast: true },
  });

  return {
    macros: macros?.macros,
    macro,
    listStatus,
    macroStatus,
    addMacro,
    addStatus,
    updateMacro,
    updateStatus,
    deleteMacro,
    deleteMacroAsync,
    deleteStatus,
  };
};
